import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  FileText,
  Sparkles,
  ClipboardList,
  BookOpen,
  ChevronRight,
  LogIn,
  Heart,
  Brain,
  History,
  Activity,
  Bot,
  CheckCircle2,
  ShieldAlert,
  UserPlus,
  Leaf,
  RefreshCw,
  RotateCw,
} from 'lucide-react';
import { motion, useInView, useReducedMotion, AnimatePresence } from 'framer-motion';
import LoginPromptModal from '../../components/LoginPromptModal';
import '../../styles/treatment.css';
import '../../styles/treatment-intro.css';

const GUEST_STATS = [
  { icon: BookOpen, value: 150, suffix: '+', label: 'Herbal Medicines' },
  { icon: Activity, value: 80, suffix: '+', label: 'Yoga Asanas' },
  { icon: FileText, value: 120, suffix: '+', label: 'Clinical Conditions' },
  { icon: ClipboardList, value: 30, suffix: '+', label: 'Treatment Plans' },
];

const HOW_IT_WORKS = [
  { step: 1, icon: UserPlus, title: 'Create a patient profile', description: 'Add details once and reuse the profile across every future visit.' },
  { step: 2, icon: Sparkles, title: 'Assess Prakriti', description: 'A short questionnaire determines the Vata-Pitta-Kapha constitution behind personalized care.' },
  { step: 3, icon: Brain, title: 'Generate AI recommendations', description: 'The Vedic AI Clinical Engine maps condition + Prakriti to herbs, yoga, and diet with full rationale.' },
  { step: 4, icon: ClipboardList, title: 'Save an active plan', description: 'Turn a recommendation into a daily routine, automatically organized morning to night.' },
  { step: 5, icon: History, title: 'Log outcomes over time', description: 'Track effectiveness, side effects, and doctor notes at every follow-up.' },
  { step: 6, icon: RefreshCw, title: 'AI-assisted adjustment', description: 'The AI reviews outcome history and proposes plan changes — every edit versioned.' },
];

// Feature capability cards — each maps a classical concept to what the tool does (flip reveal).
const FEATURES = [
  {
    path: '/treatment/assess',
    label: 'Prakriti Assessment',
    icon: Leaf,
    sanskrit: 'प्रकृति',
    classical: 'Prakriti',
    modern: 'Constitution Analysis',
    highlights: ['Vata / Pitta / Kapha scoring', 'Dominant + secondary type', 'Per-dosha diet & lifestyle'],
    cta: 'Try assessment',
  },
  {
    path: '/treatment/recommendations',
    label: 'AI Recommendations',
    icon: Brain,
    sanskrit: 'चिकित्सा',
    classical: 'Chikitsa',
    modern: 'AI Treatment Engine',
    highlights: ['Samprapti rationale', 'Herb dosage + Anupana', 'Pathya / Apathya diet split'],
    cta: 'Get recommendations',
  },
  {
    path: '/treatment/plans',
    label: 'Treatment Plans',
    icon: ClipboardList,
    sanskrit: 'दिनचर्या',
    classical: 'Dinacharya',
    modern: 'Daily Care Plans',
    highlights: ['Routine visualizer', 'Outcome logging', 'AI-assisted adjustment', 'Revision history'],
    cta: 'View plans',
  },
  {
    path: '/treatment/catalog',
    label: 'AYUSH Catalog',
    icon: BookOpen,
    sanskrit: 'द्रव्यगुण',
    classical: 'Dravyaguna',
    modern: 'Reference Library',
    highlights: ['Rasa / Virya / Vipaka / Guna', 'Dosha-tagged asanas', 'Prakriti-filtered diet'],
    cta: 'Browse catalog',
  },
  {
    path: '/treatment/patients',
    label: 'Patient Profiles',
    icon: Users,
    sanskrit: 'रोगी',
    classical: 'Rogi',
    modern: 'Patient Records',
    highlights: ['Reusable profiles', 'Scoped assessments', 'One organized workspace'],
    cta: 'Get started',
  },
];

const AI_DEMO_QUERY = 'What does Ayurveda recommend for acid reflux?';
const AI_DEMO_LINES = [
  'Classical diagnosis: Amlapitta — aggravated Pitta in the digestive tract.',
  'Formulation: Avipattikar Churna, 3g twice daily (Anupana: warm water).',
  'Supporting practice: Vajrasana, 10 minutes after meals.',
];

// ---------------------------------------------------------------------------
// Animated stitching divider — draws itself in as it scrolls into view.
// ---------------------------------------------------------------------------
const StitchDivider = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduce = useReducedMotion();

  return (
    <div ref={ref} className="ti-stitch" aria-hidden="true">
      <svg viewBox="0 0 600 20" preserveAspectRatio="none">
        <defs>
          <linearGradient id="ti-stitch-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--primary-color)" />
            <stop offset="50%" stopColor="var(--secondary-color)" />
            <stop offset="100%" stopColor="var(--accent-pink)" />
          </linearGradient>
        </defs>
        <motion.path
          d="M0,10 Q15,2 30,10 T60,10 T90,10 T120,10 T150,10 T180,10 T210,10 T240,10 T270,10 T300,10 T330,10 T360,10 T390,10 T420,10 T450,10 T480,10 T510,10 T540,10 T570,10 T600,10"
          fill="none"
          stroke="url(#ti-stitch-grad)"
          strokeWidth="2"
          strokeDasharray="7 7"
          strokeLinecap="round"
          initial={reduce ? { pathLength: 1 } : { pathLength: 0 }}
          animate={inView ? { pathLength: 1 } : {}}
          transition={{ duration: reduce ? 0 : 1.4, ease: 'easeInOut' }}
        />
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Count-up number, triggers once in view.
// ---------------------------------------------------------------------------
const CountUp = ({ value, suffix = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) { setDisplay(value); return; }
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setDisplay(Math.round((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduce, value]);

  return <span ref={ref} className="treatment-stat-value">{display}{suffix}</span>;
};

// ---------------------------------------------------------------------------
// Radial confidence gauge — fills in + counts up when scrolled into view.
// ---------------------------------------------------------------------------
const ConfidenceGauge = ({ percent = 92, active }) => {
  const reduce = useReducedMotion();
  const R = 42;
  const C = 2 * Math.PI * R;
  const [val, setVal] = useState(reduce ? percent : 0);

  useEffect(() => {
    if (!active) return;
    if (reduce) { setVal(percent); return; }
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setVal(Math.round(p * percent));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, reduce, percent]);

  return (
    <div className="ti-gauge">
      <svg viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={R} className="ti-gauge-track" />
        <circle
          cx="50" cy="50" r={R}
          className="ti-gauge-fill"
          strokeDasharray={C}
          strokeDashoffset={C - (val / 100) * C}
        />
      </svg>
      <div className="ti-gauge-center">
        <span className="ti-gauge-value">{val}%</span>
        <span className="ti-gauge-cap">AI Confidence</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Conversational AI demo — query bubble → typing → streamed reply → result card.
// Replayable via a "Run again" control.
// ---------------------------------------------------------------------------
const AiConversationDemo = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: '-120px' });
  const reduce = useReducedMotion();
  const [stage, setStage] = useState('idle'); // idle → query → typing → response → result
  const [lines, setLines] = useState([]);
  const [runId, setRunId] = useState(0);

  // Kick off the sequence when it scrolls into view (or on replay).
  useEffect(() => {
    if (!inView) return;
    setLines([]);
    setStage('idle');

    if (reduce) {
      setLines(AI_DEMO_LINES);
      setStage('result');
      return;
    }

    const t = [];
    t.push(setTimeout(() => setStage('query'), 250));
    t.push(setTimeout(() => setStage('typing'), 1100));
    t.push(setTimeout(() => setStage('response'), 2500));
    return () => t.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, runId]);

  // Stream response lines one at a time.
  useEffect(() => {
    if (stage !== 'response') return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setLines(AI_DEMO_LINES.slice(0, i));
      if (i >= AI_DEMO_LINES.length) {
        clearInterval(id);
        setTimeout(() => setStage('result'), 550);
      }
    }, 700);
    return () => clearInterval(id);
  }, [stage]);

  return (
    <div ref={ref} className="ti-demo card">
      <div className="ti-demo-head">
        <span className="ti-demo-title">
          <Bot size={18} />
          Vedic AI Clinical Engine
        </span>
        <span className="ti-demo-status">
          <span className="ti-demo-dot" />
          Online
        </span>
      </div>

      <div className="ti-chat">
        <AnimatePresence>
          {stage !== 'idle' && (
            <motion.div
              key="q"
              className="ti-bubble ti-bubble-user"
              initial={reduce ? false : { opacity: 0, y: 12, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.35 }}
            >
              {AI_DEMO_QUERY}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {stage === 'typing' && (
            <motion.div
              key="t"
              className="ti-bubble ti-bubble-ai ti-typing"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <span className="ti-typing-dot" />
              <span className="ti-typing-dot" />
              <span className="ti-typing-dot" />
            </motion.div>
          )}
        </AnimatePresence>

        {(stage === 'response' || stage === 'result') && lines.length > 0 && (
          <motion.div
            className="ti-bubble ti-bubble-ai"
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {lines.map((l, i) => (
              <motion.p
                key={i}
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {l}
              </motion.p>
            ))}
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {stage === 'result' && (
          <motion.div
            className="ti-result"
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="ti-result-terms">
              <div className="ti-term ti-term-classical">
                <span className="ti-term-label">Classical (Shastra)</span>
                <span className="ti-term-value">Amlapitta</span>
              </div>
              <ChevronRight size={20} className="ti-term-arrow" />
              <div className="ti-term ti-term-modern">
                <span className="ti-term-label">Modern Equivalent</span>
                <span className="ti-term-value">Acid Reflux / GERD</span>
              </div>
            </div>

            <div className="ti-result-body">
              <div className="ti-result-diet">
                <div className="ti-pathya"><CheckCircle2 size={15} /> Pathya: Coconut water, cooked greens</div>
                <div className="ti-apathya"><ShieldAlert size={15} /> Apathya: Fried, spicy, sour foods</div>
              </div>
              <ConfidenceGauge percent={92} active={stage === 'result'} />
            </div>

            {!reduce && (
              <button type="button" className="ti-replay" onClick={() => setRunId((n) => n + 1)}>
                <RotateCw size={14} /> Run again
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Flip card — front: classical term + Sanskrit; back: modern mapping + CTA.
// ---------------------------------------------------------------------------
const FlipCard = ({ item, index, onSelect }) => {
  const Icon = item.icon;
  const [flipped, setFlipped] = useState(false);
  const reduce = useReducedMotion();

  return (
    <motion.div
      className={`ti-flip ${reduce ? 'ti-flip-static' : ''}`}
      initial={reduce ? false : { opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      onMouseEnter={() => !reduce && setFlipped(true)}
      onMouseLeave={() => !reduce && setFlipped(false)}
      onClick={() => setFlipped((f) => !f)}
      tabIndex={0}
      role="button"
      aria-label={`${item.label} — ${item.modern}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFlipped((f) => !f); } }}
    >
      <div className={`ti-flip-inner ${flipped ? 'is-flipped' : ''}`}>
        {/* Front */}
        <div className="ti-flip-face ti-flip-front card">
          <div className="ti-flip-sanskrit">{item.sanskrit}</div>
          <div className="ti-flip-icon"><Icon size={26} /></div>
          <h3>{item.label}</h3>
          <span className="ti-flip-classical">{item.classical}</span>
          <span className="ti-flip-hint">Hover to reveal →</span>
        </div>

        {/* Back */}
        <div className="ti-flip-face ti-flip-back card">
          <span className="ti-flip-modern-label">Modern equivalent</span>
          <h3>{item.modern}</h3>
          <ul className="ti-flip-highlights">
            {item.highlights.map((h) => (
              <li key={h}><CheckCircle2 size={13} /> {h}</li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn-outline ti-flip-cta"
            onClick={(e) => { e.stopPropagation(); onSelect(item.path); }}
          >
            {item.cta}
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const TreatmentIntro = () => {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginReturnPath, setLoginReturnPath] = useState('/treatment');

  const openLoginFor = (path) => {
    setLoginReturnPath(path);
    setShowLoginPrompt(true);
  };

  const reduce = useReducedMotion();
  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-60px' },
    transition: { duration: 0.5, delay },
  });

  return (
    <div className="treatment-intro treatment-page">
      {/* Hero (unchanged) */}
      <section className="treatment-intro-hero">
        <div className="container">
          <div className="treatment-intro-hero-content">
            <div className="treatment-intro-hero-icon">
              <Heart size={40} />
            </div>
            <h1>Personalized AYUSH Care</h1>
            <p className="treatment-intro-tagline">
              AI-enabled treatment recommendations based on Prakriti, conditions, and evidence from the AYUSH ecosystem. Explore what you can do—then sign in to start.
            </p>
            <button
              type="button"
              className="btn btn-primary treatment-intro-cta-main"
              onClick={() => openLoginFor('/treatment')}
            >
              <LogIn size={20} />
              Sign in to get started
            </button>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="section section-alt treatment-intro-stats-section">
        <div className="container">
          <div className="treatment-stats-grid">
            {GUEST_STATS.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  className="treatment-stat-card"
                  initial={reduce ? false : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                >
                  <Icon size={28} />
                  <CountUp value={stat.value} suffix={stat.suffix} />
                  <span className="treatment-stat-label">{stat.label}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <StitchDivider />

      {/* AI engine in action — the centerpiece */}
      <section className="section">
        <div className="container">
          <motion.div {...fade()}>
            <h2 className="section-title treatment-intro-section-title">
              <Bot size={28} />
              See the AI engine in action
            </h2>
            <p className="treatment-intro-section-desc">
              Ask about a condition. The engine grounds it in classical terminology, then maps it to modern care.
            </p>
          </motion.div>
          <motion.div {...fade(0.1)}>
            <AiConversationDemo />
          </motion.div>
        </div>
      </section>

      <StitchDivider />

      {/* How it works — the workflow */}
      <section className="section section-alt">
        <div className="container">
          <motion.div {...fade()}>
            <h2 className="section-title treatment-intro-section-title">
              <Activity size={28} />
              From first visit to lasting care
            </h2>
            <p className="treatment-intro-section-desc">
              A structured, patient-centric workflow — six steps, fully guided.
            </p>
          </motion.div>
          <div className="ti-steps">
            {HOW_IT_WORKS.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.step}
                  className="ti-step card"
                  initial={reduce ? false : { opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                >
                  <div className="ti-step-top">
                    <span className="ti-step-num">{item.step}</span>
                    <span className="ti-step-icon"><Icon size={20} /></span>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <StitchDivider />

      {/* What you can do — capability flip cards */}
      <section className="section">
        <div className="container">
          <motion.div {...fade()}>
            <h2 className="section-title treatment-intro-section-title">
              <Sparkles size={28} />
              Ancient wisdom, modern tools
            </h2>
            <p className="treatment-intro-section-desc">
              Each capability bridges a classical concept to a working feature. Hover a card to see the mapping.
            </p>
          </motion.div>
          <div className="ti-flip-grid">
            {FEATURES.map((item, i) => (
              <FlipCard key={item.path} item={item} index={i} onSelect={openLoginFor} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust / credibility — calm, restrained */}
      <motion.section
        className="section section-alt"
        initial={reduce ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          <div className="ti-trust card">
            <div className="ti-trust-icon">
              <Brain size={30} />
            </div>
            <div className="ti-trust-body">
              <h3>Built to support clinical judgment</h3>
              <p>
                As outcomes are logged during follow-ups, the AI reviews the history and proposes plan
                adjustments — swapping formulations or dosages when needed. Every change, AI-assisted or manual,
                is preserved in a full revision history you can compare side-by-side.
              </p>
              <p className="ti-trust-disclaimer">
                AI recommendations assist, and never replace, professional medical advice and diagnosis.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Bottom CTA (unchanged) */}
      <section className="section">
        <div className="container">
          <div className="treatment-intro-bottom-cta treatment-card treatment-card-cta">
            <Sparkles size={32} />
            <div>
              <h3>Ready to start?</h3>
              <p>Sign in or create a free account to use Patient profiles, Prakriti assessment, and personalized recommendations.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => openLoginFor('/treatment')}
              >
                <LogIn size={18} />
                Sign in or create account
              </button>
            </div>
          </div>
        </div>
      </section>

      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        returnPath={loginReturnPath}
        title="Sign in to use AYUSH Care"
        message="Create a free account or sign in to access patient profiles, Prakriti assessment, recommendations, and treatment plans."
      />
    </div>
  );
};

export default TreatmentIntro;
